import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowRight, ArrowLeft, Building, User, Mail, Phone, 
  Users, Target, Calendar, CheckCircle, Loader2, Sparkles,
  AlertTriangle, TrendingUp, Shield, Zap
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Analyse de risque IA par secteur
const AI_RISK_DATA = {
  "tech": { risk: 85, urgency: "CRITIQUE", message: "L'IA est déjà omniprésente dans la tech. Vos concurrents l'utilisent certainement." },
  "finance": { risk: 78, urgency: "ÉLEVÉ", message: "Automatisation des analyses, trading algorithmique, détection de fraude... L'IA révolutionne la finance." },
  "conseil": { risk: 72, urgency: "ÉLEVÉ", message: "Les cabinets de conseil qui maîtrisent l'IA offrent des livrables 3x plus rapides." },
  "marketing": { risk: 80, urgency: "CRITIQUE", message: "Génération de contenu, personnalisation, analyse prédictive : l'IA transforme le marketing." },
  "rh": { risk: 65, urgency: "MODÉRÉ", message: "Recrutement assisté, gestion des talents, formation... L'IA optimise les RH." },
  "industrie": { risk: 70, urgency: "ÉLEVÉ", message: "Maintenance prédictive, optimisation de production, contrôle qualité IA." },
  "sante": { risk: 68, urgency: "ÉLEVÉ", message: "Diagnostic assisté, recherche médicale, gestion hospitalière... L'IA sauve des vies." },
  "retail": { risk: 75, urgency: "ÉLEVÉ", message: "Personnalisation, gestion des stocks, prévision des ventes : l'IA domine le retail." },
  "immobilier": { risk: 55, urgency: "MODÉRÉ", message: "Estimations automatisées, matching acquéreurs, visite virtuelle IA." },
  "juridique": { risk: 62, urgency: "MODÉRÉ", message: "Analyse de contrats, recherche jurisprudentielle, rédaction assistée." },
  "education": { risk: 58, urgency: "MODÉRÉ", message: "Personnalisation des parcours, correction automatique, tuteurs IA." },
  "autre": { risk: 60, urgency: "MODÉRÉ", message: "L'IA impacte tous les secteurs. Anticipez plutôt que de subir." }
};

const TunnelVentePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedFormat = searchParams.get("format");

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiRisk, setAiRisk] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    company_size: "",
    sector: "",
    sector_detail: "",
    ai_knowledge_level: 25,
    current_ai_usage: "none",
    training_format: preselectedFormat || "",
    participant_count: 10,
    specific_needs: "",
    budget_range: "",
    preferred_dates: "",
    urgency: "normal"
  });

  const steps = [
    { id: 1, name: "Votre entreprise", icon: <Building className="w-5 h-5" /> },
    { id: 2, name: "Analyse IA", icon: <Target className="w-5 h-5" /> },
    { id: 3, name: "Votre projet", icon: <Calendar className="w-5 h-5" /> },
    { id: 4, name: "Récapitulatif", icon: <CheckCircle className="w-5 h-5" /> }
  ];

  const companySizes = [
    { value: "1-10", label: "1-10 salariés", icon: "👤" },
    { value: "11-50", label: "11-50 salariés", icon: "👥" },
    { value: "51-200", label: "51-200 salariés", icon: "🏢" },
    { value: "201-500", label: "201-500 salariés", icon: "🏛️" },
    { value: "500+", label: "Plus de 500", icon: "🌐" }
  ];

  const sectors = [
    { value: "tech", label: "Tech & Software" },
    { value: "finance", label: "Finance & Banque" },
    { value: "conseil", label: "Conseil & Services" },
    { value: "marketing", label: "Marketing & Communication" },
    { value: "rh", label: "Ressources Humaines" },
    { value: "industrie", label: "Industrie & Manufacturing" },
    { value: "sante", label: "Santé & Pharma" },
    { value: "retail", label: "Retail & E-commerce" },
    { value: "immobilier", label: "Immobilier" },
    { value: "juridique", label: "Juridique" },
    { value: "education", label: "Éducation & Formation" },
    { value: "autre", label: "Autre secteur" }
  ];

  const aiUsageOptions = [
    { value: "none", label: "Aucune utilisation", description: "L'IA n'est pas encore utilisée" },
    { value: "individual", label: "Usage individuel", description: "Quelques personnes utilisent ChatGPT etc." },
    { value: "partial", label: "Usage partiel", description: "Certaines équipes ont intégré l'IA" },
    { value: "widespread", label: "Usage répandu", description: "L'IA est utilisée dans plusieurs services" }
  ];

  const trainingFormats = [
    { value: "masterclass_half", label: "Masterclass Découverte", duration: "Demi-journée", price: "750€ HT", ideal: "Première découverte" },
    { value: "masterclass_full", label: "Masterclass Approfondie", duration: "Journée complète", price: "1 500€ HT", ideal: "Formation complète", featured: true },
    { value: "individual", label: "Formation Individuelle", duration: "2h/personne", price: "Sur devis", ideal: "COMEX, dirigeants" },
    { value: "workshop_tour", label: "Ateliers Ludiques", duration: "2h/atelier", price: "Sur devis", ideal: "Grandes équipes" }
  ];

  const urgencyOptions = [
    { value: "urgent", label: "Urgent", description: "Dans les 2 semaines" },
    { value: "normal", label: "Normal", description: "Dans le mois" },
    { value: "flexible", label: "Flexible", description: "Dans les 3 mois" }
  ];

  // Calculate AI risk when sector changes
  useEffect(() => {
    if (formData.sector && formData.sector_detail) {
      const baseRisk = AI_RISK_DATA[formData.sector] || AI_RISK_DATA["autre"];
      // Adjust based on AI knowledge - lower knowledge = higher risk
      const knowledgeAdjustment = (100 - formData.ai_knowledge_level) / 10;
      const adjustedRisk = Math.min(100, baseRisk.risk + knowledgeAdjustment);
      
      setAiRisk({
        ...baseRisk,
        risk: Math.round(adjustedRisk),
        sector: formData.sector_detail
      });
    }
  }, [formData.sector, formData.sector_detail, formData.ai_knowledge_level]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        // Seuls les champs essentiels sont obligatoires
        return formData.company_name && formData.contact_name && formData.email;
      case 2:
        // Étape 2 optionnelle - on peut passer même sans remplir
        return true;
      case 3:
        // Étape 3 optionnelle - on peut passer même sans remplir
        return true;
      default:
        return true;
    }
  };

  // Fonction pour sauter directement au récapitulatif
  const handleSkipToQuote = () => {
    // Vérifier seulement les infos de contact essentielles
    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error("Merci de renseigner au moins votre entreprise, nom et email");
      setStep(1);
      return;
    }
    setStep(4);
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error("Veuillez remplir tous les champs obligatoires");
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const prospectData = {
        ...formData,
        ai_knowledge_level: `${formData.ai_knowledge_level}%`
      };
      const prospectResponse = await axios.post(`${API}/prospects`, prospectData);
      const prospect = prospectResponse.data;

      const quoteResponse = await axios.post(`${API}/quotes/from-prospect/${prospect.id}`);
      const quote = quoteResponse.data;

      toast.success("Votre devis a été généré avec succès !");
      navigate(`/devis/${quote.id}`);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getKnowledgeLabel = (value) => {
    if (value < 20) return "Débutant";
    if (value < 40) return "Novice";
    if (value < 60) return "Intermédiaire";
    if (value < 80) return "Avancé";
    return "Expert";
  };

  const getRiskColor = (risk) => {
    if (risk >= 75) return "text-red-500";
    if (risk >= 60) return "text-amber-500";
    return "text-green-500";
  };

  const getRiskBg = (risk) => {
    if (risk >= 75) return "bg-red-500/10 border-red-500/30";
    if (risk >= 60) return "bg-amber-500/10 border-amber-500/30";
    return "bg-green-500/10 border-green-500/30";
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="tunnel-page">
      <Navigation />

      <div className="pt-24 sm:pt-32 pb-16 sm:pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 sm:mb-12"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              Votre <span className="text-[#007AFF]">Devis</span> Personnalisé
            </h1>
            <p className="text-white/60 text-sm sm:text-base">
              Répondez à quelques questions pour recevoir votre devis sur mesure
            </p>
          </motion.div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8 sm:mb-12 overflow-x-auto pb-2">
            {steps.map((s, index) => (
              <div key={s.id} className="flex items-center flex-shrink-0">
                <div 
                  className={`step-dot ${
                    step > s.id ? 'completed' : step === s.id ? 'active' : 'pending'
                  }`}
                  data-testid={`step-${s.id}`}
                >
                  {step > s.id ? <CheckCircle className="w-4 h-4" /> : s.id}
                </div>
                {index < steps.length - 1 && (
                  <div className={`step-line w-8 sm:w-16 md:w-24 mx-1 sm:mx-2 ${step > s.id ? 'completed' : ''}`} />
                )}
              </div>
            ))}
          </div>

          {/* Form Content */}
          <motion.div 
            className="glass-card p-6 sm:p-8 md:p-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <AnimatePresence mode="wait">
              {/* Step 1: Company Info */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Parlez-nous de vous</h2>
                    <p className="text-white/50 text-sm">Informations de contact</p>
                  </div>

                  <div className="space-y-4 bg-white/5 p-4 sm:p-6 rounded-xl border border-white/10">
                    <div>
                      <Label htmlFor="company_name">Nom de l'entreprise *</Label>
                      <Input
                        id="company_name"
                        value={formData.company_name}
                        onChange={(e) => handleChange("company_name", e.target.value)}
                        placeholder="Votre entreprise"
                        className="mt-2"
                        data-testid="input-company-name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact_name">Votre nom *</Label>
                      <Input
                        id="contact_name"
                        value={formData.contact_name}
                        onChange={(e) => handleChange("contact_name", e.target.value)}
                        placeholder="Prénom Nom"
                        className="mt-2"
                        data-testid="input-contact-name"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email professionnel *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="vous@entreprise.com"
                          className="mt-2"
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Téléphone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          placeholder="06 12 34 56 78"
                          className="mt-2"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    {/* Company Size - Radio Cards */}
                    <div>
                      <Label className="mb-3 block">Taille de l'entreprise</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                        {companySizes.map((size) => (
                          <button
                            key={size.value}
                            type="button"
                            onClick={() => handleChange("company_size", size.value)}
                            className={`p-3 sm:p-4 rounded-xl border text-center transition-all ${
                              formData.company_size === size.value
                                ? 'border-[#007AFF] bg-[#007AFF]/10 text-white'
                                : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
                            }`}
                            data-testid={`size-${size.value}`}
                          >
                            <span className="text-xl sm:text-2xl block mb-1">{size.icon}</span>
                            <span className="text-xs sm:text-sm">{size.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: AI Analysis */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Analyse de votre exposition IA</h2>
                    <p className="text-white/50 text-sm">Évaluons ensemble votre niveau de risque</p>
                  </div>

                  <div className="space-y-6 bg-white/5 p-4 sm:p-6 rounded-xl border border-white/10">
                    {/* Sector Selection */}
                    <div>
                      <Label className="mb-3 block">Secteur d'activité</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {sectors.map((sector) => (
                          <button
                            key={sector.value}
                            type="button"
                            onClick={() => handleChange("sector", sector.value)}
                            className={`p-3 rounded-lg border text-left transition-all text-sm ${
                              formData.sector === sector.value
                                ? 'border-[#007AFF] bg-[#007AFF]/10 text-white'
                                : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
                            }`}
                            data-testid={`sector-${sector.value}`}
                          >
                            {sector.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Sector Detail */}
                    <div>
                      <Label htmlFor="sector_detail">Décrivez votre activité en quelques mots</Label>
                      <Input
                        id="sector_detail"
                        value={formData.sector_detail}
                        onChange={(e) => handleChange("sector_detail", e.target.value)}
                        placeholder="Ex: Cabinet de conseil en stratégie, 30 consultants..."
                        className="mt-2"
                        data-testid="input-sector-detail"
                      />
                    </div>

                    {/* AI Knowledge Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <Label>Niveau de connaissance IA dans votre entreprise</Label>
                        <span className="text-[#007AFF] font-semibold">{formData.ai_knowledge_level}% - {getKnowledgeLabel(formData.ai_knowledge_level)}</span>
                      </div>
                      <Slider
                        value={[formData.ai_knowledge_level]}
                        onValueChange={(value) => handleChange("ai_knowledge_level", value[0])}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="slider-knowledge"
                      />
                      <div className="flex justify-between text-xs text-white/40 mt-2">
                        <span>Débutant</span>
                        <span>Expert</span>
                      </div>
                    </div>

                    {/* Current AI Usage - Radio Group */}
                    <div>
                      <Label className="mb-3 block">Usage actuel de l'IA</Label>
                      <RadioGroup
                        value={formData.current_ai_usage}
                        onValueChange={(value) => handleChange("current_ai_usage", value)}
                        className="space-y-2"
                      >
                        {aiUsageOptions.map((option) => (
                          <div
                            key={option.value}
                            className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                              formData.current_ai_usage === option.value
                                ? 'border-[#007AFF] bg-[#007AFF]/10'
                                : 'border-white/10 bg-white/5 hover:border-white/20'
                            }`}
                            onClick={() => handleChange("current_ai_usage", option.value)}
                          >
                            <RadioGroupItem value={option.value} id={option.value} />
                            <div className="flex-1">
                              <Label htmlFor={option.value} className="cursor-pointer text-white font-medium">
                                {option.label}
                              </Label>
                              <p className="text-white/50 text-xs">{option.description}</p>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    {/* AI Risk Analysis Card */}
                    {aiRisk && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-4 sm:p-6 rounded-xl border ${getRiskBg(aiRisk.risk)}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full ${getRiskBg(aiRisk.risk)} flex items-center justify-center flex-shrink-0`}>
                            {aiRisk.risk >= 75 ? (
                              <AlertTriangle className={`w-6 h-6 ${getRiskColor(aiRisk.risk)}`} />
                            ) : aiRisk.risk >= 60 ? (
                              <TrendingUp className={`w-6 h-6 ${getRiskColor(aiRisk.risk)}`} />
                            ) : (
                              <Shield className={`w-6 h-6 ${getRiskColor(aiRisk.risk)}`} />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-white">
                                Taux d'exposition IA : <span className={getRiskColor(aiRisk.risk)}>{aiRisk.risk}%</span>
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRiskBg(aiRisk.risk)} ${getRiskColor(aiRisk.risk)}`}>
                                {aiRisk.urgency}
                              </span>
                            </div>
                            <p className="text-white/70 text-sm mb-3">{aiRisk.message}</p>
                            <p className="text-white/50 text-xs">
                              <strong>Recommandation :</strong> Formez vos équipes rapidement pour transformer cette menace en opportunité.
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Step 3: Project */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Votre projet de formation</h2>
                    <p className="text-white/50 text-sm">Choisissez le format adapté à vos besoins</p>
                  </div>

                  <div className="space-y-6 bg-white/5 p-4 sm:p-6 rounded-xl border border-white/10">
                    {/* Training Format - Visual Cards */}
                    <div>
                      <Label className="mb-3 block">Format de formation souhaité</Label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {trainingFormats.map((format) => (
                          <button
                            key={format.value}
                            type="button"
                            onClick={() => handleChange("training_format", format.value)}
                            className={`p-4 rounded-xl border text-left transition-all relative ${
                              formData.training_format === format.value
                                ? 'border-[#007AFF] bg-[#007AFF]/10'
                                : 'border-white/10 bg-white/5 hover:border-white/20'
                            }`}
                            data-testid={`format-${format.value}`}
                          >
                            {format.featured && (
                              <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-[#007AFF] rounded-full text-xs font-semibold">
                                RECOMMANDÉ
                              </span>
                            )}
                            <h4 className="text-white font-semibold mb-1">{format.label}</h4>
                            <p className="text-white/50 text-xs mb-2">{format.duration}</p>
                            <div className="flex justify-between items-center">
                              <span className="text-[#007AFF] font-semibold text-sm">{format.price}</span>
                              <span className="text-white/40 text-xs">{format.ideal}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Participant Count - Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <Label>Nombre de participants</Label>
                        <span className="text-[#007AFF] font-semibold text-lg">{formData.participant_count}</span>
                      </div>
                      <Slider
                        value={[formData.participant_count]}
                        onValueChange={(value) => handleChange("participant_count", value[0])}
                        min={1}
                        max={100}
                        step={1}
                        className="w-full"
                        data-testid="slider-participants"
                      />
                      <div className="flex justify-between text-xs text-white/40 mt-2">
                        <span>1</span>
                        <span>50</span>
                        <span>100</span>
                      </div>
                    </div>

                    {/* Urgency - Radio Cards */}
                    <div>
                      <Label className="mb-3 block">Urgence de votre besoin</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {urgencyOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleChange("urgency", option.value)}
                            className={`p-3 rounded-lg border text-center transition-all ${
                              formData.urgency === option.value
                                ? 'border-[#007AFF] bg-[#007AFF]/10'
                                : 'border-white/10 bg-white/5 hover:border-white/20'
                            }`}
                          >
                            <span className="text-white font-medium text-sm block">{option.label}</span>
                            <span className="text-white/50 text-xs">{option.description}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Dates */}
                    <div>
                      <Label htmlFor="preferred_dates">Dates souhaitées</Label>
                      <Input
                        id="preferred_dates"
                        value={formData.preferred_dates}
                        onChange={(e) => handleChange("preferred_dates", e.target.value)}
                        placeholder="Ex: Février 2026, flexible..."
                        className="mt-2"
                        data-testid="input-dates"
                      />
                    </div>

                    {/* Specific Needs */}
                    <div>
                      <Label htmlFor="specific_needs">Besoins spécifiques</Label>
                      <Textarea
                        id="specific_needs"
                        value={formData.specific_needs}
                        onChange={(e) => handleChange("specific_needs", e.target.value)}
                        placeholder="Décrivez vos attentes particulières, contraintes, objectifs..."
                        className="mt-2 min-h-[100px]"
                        data-testid="input-needs"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Summary */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Récapitulatif</h2>
                    <p className="text-white/50 text-sm">Vérifiez vos informations avant de valider</p>
                  </div>

                  <div className="space-y-4">
                    {/* Company Info */}
                    <div className="p-4 bg-white/5 rounded-xl">
                      <h3 className="text-[#007AFF] font-semibold mb-3 flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Entreprise
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-white/50">Entreprise:</span>
                          <span className="text-white ml-2">{formData.company_name}</span>
                        </div>
                        <div>
                          <span className="text-white/50">Contact:</span>
                          <span className="text-white ml-2">{formData.contact_name}</span>
                        </div>
                        <div>
                          <span className="text-white/50">Email:</span>
                          <span className="text-white ml-2">{formData.email}</span>
                        </div>
                        <div>
                          <span className="text-white/50">Secteur:</span>
                          <span className="text-white ml-2">{formData.sector_detail || sectors.find(s => s.value === formData.sector)?.label}</span>
                        </div>
                      </div>
                    </div>

                    {/* AI Risk Summary */}
                    {aiRisk && (
                      <div className={`p-4 rounded-xl border ${getRiskBg(aiRisk.risk)}`}>
                        <h3 className={`font-semibold mb-2 flex items-center gap-2 ${getRiskColor(aiRisk.risk)}`}>
                          <AlertTriangle className="w-4 h-4" />
                          Exposition IA : {aiRisk.risk}%
                        </h3>
                        <p className="text-white/70 text-sm">Niveau de connaissance : {formData.ai_knowledge_level}%</p>
                      </div>
                    )}

                    {/* Project Info */}
                    <div className="p-4 bg-white/5 rounded-xl">
                      <h3 className="text-[#007AFF] font-semibold mb-3 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Projet
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-white/50">Format:</span>
                          <span className="text-white ml-2">{trainingFormats.find(f => f.value === formData.training_format)?.label}</span>
                        </div>
                        <div>
                          <span className="text-white/50">Participants:</span>
                          <span className="text-white ml-2">{formData.participant_count}</span>
                        </div>
                        {formData.preferred_dates && (
                          <div>
                            <span className="text-white/50">Dates:</span>
                            <span className="text-white ml-2">{formData.preferred_dates}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-white/50">Urgence:</span>
                          <span className="text-white ml-2">{urgencyOptions.find(u => u.value === formData.urgency)?.label}</span>
                        </div>
                      </div>
                    </div>

                    {/* Price Estimate */}
                    <div className="p-4 bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-xl">
                      <h3 className="text-white font-semibold mb-2">Tarif indicatif</h3>
                      <p className="text-2xl font-bold text-[#007AFF]">
                        Base: 1 500€ HT / jour
                      </p>
                      <p className="text-white/50 text-sm mt-1">
                        Le devis détaillé vous sera envoyé par Lucie sous 24h
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex flex-col gap-4 mt-8 pt-6 border-t border-white/10">
              {/* Bouton Devis immédiat - visible sur toutes les étapes sauf la 4 */}
              {step < 4 && (
                <button
                  onClick={handleSkipToQuote}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25"
                  data-testid="btn-skip-to-quote"
                >
                  <Zap className="w-5 h-5" />
                  Devis immédiat
                  <span className="text-amber-100 text-sm font-normal ml-1">(passer les questions)</span>
                </button>
              )}

              <div className="flex items-center justify-between">
                {step > 1 ? (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
                    data-testid="btn-back"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour
                  </button>
                ) : (
                  <div />
                )}

                {step < 4 ? (
                  <button
                    onClick={handleNext}
                    className="btn-primary flex items-center gap-2"
                    data-testid="btn-next"
                  >
                    Continuer
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                    data-testid="btn-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Génération...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Obtenir mon devis
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          <p className="text-center text-white/30 text-xs mt-6">
            Entièrement réalisé par l'Intelligence Artificielle
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TunnelVentePage;
